<template>
  <iVideoTile
    :label="displayName"
    :speaking="isSpeaking && !showMicOff"
    :hand-raised="showHandRaised"
    :pinned="isPinned"
    :fit="tile?.isScreenShare ? 'contain' : 'cover'"
    :audio="audioBadge"
    class="stage-tile"
    :data-role="role"
    :data-peer-id="tile?.id"
    @click="$emit('click', tile)"
  >
    <iTilePresenting
      v-if="tile?.isLocal && tile?.isScreenShare"
      :compact="role !== 'primary'"
      @stop="$emit('stop-presenting')"
    />
    <ScreenShareViewport
      v-else-if="tile?.isScreenShare"
      :peer-id="tile?.id"
      :interactive="role === 'primary'"
      :video-track="tile?.videoTrack || null"
      :stream="tile?.stream || null"
      :is-muted="true"
      :flip-horizontally="false"
      :sink-id="sinkId"
    />
    <iVideoTileAvatar
      v-else-if="showAvatarFallback || showVideoPaused"
      :name="displayName"
      data-test="tile-avatar"
    />
    <MediaTrackRenderer
      v-else
      :video-track="tile?.videoTrack || null"
      :stream="tile?.stream || null"
      :is-muted="true"
      :flip-horizontally="flipHorizontally"
      :sink-id="sinkId"
    />
    <template #status>
      <iRow
        v-if="showHandRaised"
        width="hug"
        vertical-align="middle"
        data-test="tile-hand-raised"
      >
        <iIcon name="material-symbols:front-hand-rounded" size="standard" variant="accent" />
      </iRow>
      <iIcon
        v-if="showVideoPaused"
        name="ic:outline-signal-cellular-connected-no-internet-0-bar"
        size="small"
        variant="light"
        tooltip="Video paused: weak connection"
        data-test="tile-video-paused"
      />
      <ConnectionIndicator
        v-if="!tile?.isLocal"
        :quality="tile?.connectionQuality"
      />
    </template>
  </iVideoTile>
</template>

<script>
import MediaTrackRenderer from "%/apps/video-engine/components/video-call/shared/MediaTrackRenderer.vue";
import ScreenShareViewport from "%/apps/video-engine/components/video-call/stage/shared/ScreenShareViewport.vue";
import ConnectionIndicator from "%/apps/video-engine/components/video-call/shared/ConnectionIndicator.vue";

export default {
  name: "StageTile",
  components: {
    MediaTrackRenderer,
    ScreenShareViewport,
    ConnectionIndicator,
  },
  props: {
    tile: {
      type: Object,
      default: null,
    },
    role: {
      type: String,
      default: "grid",
    },
    isSpeaking: {
      type: Boolean,
      default: false,
    },
    isPinned: {
      type: Boolean,
      default: false,
    },
    isLocalVideoMirrored: {
      type: Boolean,
      default: true,
    },
    isCameraEnabled: {
      type: Boolean,
      default: true,
    },
    isMicrophoneEnabled: {
      type: Boolean,
      default: true,
    },
    sinkId: {
      type: String,
      default: null,
    },
  },
  emits: ["click", "stop-presenting"],
  computed: {
    displayName() {
      return this.tile?.displayName || "Participant";
    },
    flipHorizontally() {
      return Boolean(this.tile?.isLocal) && !this.tile?.isScreenShare && this.isLocalVideoMirrored;
    },
    hasVideo() {
      return Boolean(this.tile?.videoTrack) || (this.tile?.stream?.getVideoTracks?.().length || 0) > 0;
    },
    showAvatarFallback() {
      if (this.tile?.isScreenShare) {
        return false;
      }
      if (this.tile?.isLocal) {
        return !this.isCameraEnabled || !this.hasVideo;
      }

      return !this.hasVideo || Boolean(this.tile?.isVideoMuted);
    },
    showHandRaised() {
      return Boolean(this.tile?.handRaised) && !this.tile?.isScreenShare;
    },
    showVideoPaused() {
      return Boolean(this.tile?.isVideoPaused) && !this.tile?.isScreenShare;
    },
    audioBadge() {
      if (this.showMicOff) {
        return "muted";
      }

      return this.isSpeaking && !this.tile?.isScreenShare ? "speaking" : "none";
    },
    showMicOff() {
      if (this.tile?.isScreenShare) {
        return false;
      }

      return this.tile?.isLocal ? !this.isMicrophoneEnabled : Boolean(this.tile?.isAudioMuted);
    },
  },
};
</script>
